import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LazyMarkdown } from '@sorare/core/src/atoms/markdown/LazyMarkdown';
import { Card } from '@sorare/core/src/components/card/Card';
import { ANY_SPORT_CARDS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { getSportOrSharedFromSport } from '@sorare/core/src/lib/sports';

import { DumbNotification } from 'components/activity/DumbNotification';

import { commonNotificationInterfaceFragment } from '../fragments';
import type { CommonNotificationProps } from '../types';
import type { CardNotification_cardNotification } from './__generated__/index.graphql';

type Props = CommonNotificationProps & {
  notification: CardNotification_cardNotification;
};

export const CardNotification = ({ notification, ...rest }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { generateSportContextPath } = useSportContext();

  const {
    name,
    createdAt,
    anyCard: card,
    sport,
    read,
    markdownBody,
  } = notification;

  if (!card) return null;

  const link = card?.slug
    ? generateSportContextPath(ANY_SPORT_CARDS_SLUG, {
        params: {
          slug: card.slug,
        },
        sport: card.sport || Sport.FOOTBALL,
      })
    : '';

  return (
    <DumbNotification
      name={name}
      title={<LazyMarkdown data={markdownBody} />}
      userAvatar={currentUser}
      sport={getSportOrSharedFromSport(card?.sport) || sport}
      createdAt={createdAt}
      cardPicture={!!card && <Card card={card} />}
      link={link}
      read={read}
      {...rest}
    />
  );
};

CardNotification.fragments = {
  cardNotification: gql`
    fragment CardNotification_cardNotification on CardNotification {
      id
      ...Notification_notificationInterface
      anyCard {
        slug
        sport
        ...Card_anyCard
      }
    }
    ${commonNotificationInterfaceFragment}
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<CardNotification_cardNotification>,
};
